import {
  getList,
  getString,
  getOptionalString,
  getLeoDate,
  LeoDate,
  getOptionalLeoDate,
  getObject,
  getInt32,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { GetArchivedResearcherProjectsSortOrder, GetArchivedResearcherProjectsSortOrderDTO, GetArchivedResearcherProjectsSortOrderEnums } from "./getArchivedResearcherProjectsSortOrder";
import { ProjectItemsPerPage, ProjectItemsPerPageDTO } from "./projectItemsPerPage";
import { ProjectPageIndex, ProjectPageIndexDTO } from "./projectPageIndex";
import { GetArchivedResearcherProjectsPaginationResponse, GetArchivedResearcherProjectsPaginationResponseDTO } from "./getArchivedResearcherProjectsPaginationResponse";
/* eslint-disable import/export */
export abstract class GetArchivedResearcherProjectsRPC implements
  LeoRPC<GetArchivedResearcherProjectsRPC.Request, GetArchivedResearcherProjectsRPC.Response, GetArchivedResearcherProjectsRPC.Errors.Errors>
{
  abstract execute(
    request: GetArchivedResearcherProjectsRPC.Request
  ): Promise<LeoRPCResult<GetArchivedResearcherProjectsRPC.Response, GetArchivedResearcherProjectsRPC.Errors.Errors>>;
}

export namespace GetArchivedResearcherProjectsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    sortList: GetArchivedResearcherProjectsSortOrderDTO[];
    projectName?: string | null;
    creator?: string | null;
    surveys?: string | null;
    createdOnStartDate?: string | null;
    createdOnEndDate?: string | null;
    lastModifiedStartDate?: string | null;
    lastModifiedEndDate?: string | null;
    boUserId?: string | null;
    itemsPerPage: ProjectItemsPerPageDTO;
    pageIndex: ProjectPageIndexDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetArchivedResearcherProjectsRPCRequest: boolean = true;
  
    readonly sortList: GetArchivedResearcherProjectsSortOrder[];
    readonly projectName: string | null;
    readonly creator: string | null;
    readonly surveys: string | null;
    readonly createdOnStartDate: LeoDate | null;
    readonly createdOnEndDate: LeoDate | null;
    readonly lastModifiedStartDate: LeoDate | null;
    readonly lastModifiedEndDate: LeoDate | null;
    readonly boUserId: string | null;
    readonly itemsPerPage: ProjectItemsPerPage;
    readonly pageIndex: ProjectPageIndex;
  
    static readonly constraints = {
      "projectName": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
      "creator": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
      "surveys": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
      "boUserId": {
        "minSize": 3,
        "maxSize": 200,
        
        
        
        
      },
    }
  
    constructor(
    sortList: GetArchivedResearcherProjectsSortOrder[], 
    projectName: string | null = null, 
    creator: string | null = null, 
    surveys: string | null = null, 
    createdOnStartDate: LeoDate | null = null, 
    createdOnEndDate: LeoDate | null = null, 
    lastModifiedStartDate: LeoDate | null = null, 
    lastModifiedEndDate: LeoDate | null = null, 
    boUserId: string | null = null, 
    itemsPerPage: ProjectItemsPerPage, 
    pageIndex: ProjectPageIndex
    ) {
      this.sortList = sortList;
      if ( projectName !== undefined && projectName !== null) {
        Request.validateProjectName(projectName);
      }
      this.projectName = projectName;
      if ( creator !== undefined && creator !== null) {
        Request.validateCreator(creator);
      }
      this.creator = creator;
      if ( surveys !== undefined && surveys !== null) {
        Request.validateSurveys(surveys);
      }
      this.surveys = surveys;
      this.createdOnStartDate = createdOnStartDate;
      this.createdOnEndDate = createdOnEndDate;
      this.lastModifiedStartDate = lastModifiedStartDate;
      this.lastModifiedEndDate = lastModifiedEndDate;
      if ( boUserId !== undefined && boUserId !== null) {
        Request.validateBoUserId(boUserId);
      }
      this.boUserId = boUserId;
      this.itemsPerPage = itemsPerPage;
      this.pageIndex = pageIndex;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const sortList = getList<GetArchivedResearcherProjectsSortOrderDTO, GetArchivedResearcherProjectsSortOrder>(
        dto,
        "sortList",
        GetArchivedResearcherProjectsSortOrder.fromDTO,
        InvalidRequestSortListError
      );
      const projectName = getOptionalString(dto, "projectName", InvalidRequestProjectNameError);
      const creator = getOptionalString(dto, "creator", InvalidRequestCreatorError);
      const surveys = getOptionalString(dto, "surveys", InvalidRequestSurveysError);
      const createdOnStartDate = getOptionalLeoDate(dto, "createdOnStartDate", InvalidRequestCreatedOnStartDateError);
      const createdOnEndDate = getOptionalLeoDate(dto, "createdOnEndDate", InvalidRequestCreatedOnEndDateError);
      const lastModifiedStartDate = getOptionalLeoDate(dto, "lastModifiedStartDate", InvalidRequestLastModifiedStartDateError);
      const lastModifiedEndDate = getOptionalLeoDate(dto, "lastModifiedEndDate", InvalidRequestLastModifiedEndDateError);
      const boUserId = getOptionalString(dto, "boUserId", InvalidRequestBoUserIdError);
      const itemsPerPage = ProjectItemsPerPage.fromDTO(getObject(dto, "itemsPerPage", InvalidRequestItemsPerPageError));
      const pageIndex = ProjectPageIndex.fromDTO(getObject(dto, "pageIndex", InvalidRequestPageIndexError));
      return new Request(
        sortList,
        projectName,
        creator,
        surveys,
        createdOnStartDate,
        createdOnEndDate,
        lastModifiedStartDate,
        lastModifiedEndDate,
        boUserId,
        itemsPerPage,
        pageIndex
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          sortList: this.sortList.map((e: GetArchivedResearcherProjectsSortOrder) => { return e.toDTO() }),
          itemsPerPage: this.itemsPerPage.toDTO(),
          pageIndex: this.pageIndex.toDTO()
      };
      if (this.projectName !== undefined && this.projectName !== null){
        dto.projectName = this.projectName;
      }
      if (this.creator !== undefined && this.creator !== null){
        dto.creator = this.creator;
      }
      if (this.surveys !== undefined && this.surveys !== null){
        dto.surveys = this.surveys;
      }
      if (this.createdOnStartDate !== undefined && this.createdOnStartDate !== null){
        dto.createdOnStartDate = this.createdOnStartDate.toDTO();
      }
      if (this.createdOnEndDate !== undefined && this.createdOnEndDate !== null){
        dto.createdOnEndDate = this.createdOnEndDate.toDTO();
      }
      if (this.lastModifiedStartDate !== undefined && this.lastModifiedStartDate !== null){
        dto.lastModifiedStartDate = this.lastModifiedStartDate.toDTO();
      }
      if (this.lastModifiedEndDate !== undefined && this.lastModifiedEndDate !== null){
        dto.lastModifiedEndDate = this.lastModifiedEndDate.toDTO();
      }
      if (this.boUserId !== undefined && this.boUserId !== null){
        dto.boUserId = this.boUserId;
      }
      return dto;
    }
    private static validateProjectName = (projectName: string): void => {
      if(projectName.length < 1) {
        throw new InvalidRequestProjectNameMinSizeError(
          1,
          `Attribute projectName is too short. Size is ${projectName.length}. Min size is 1.`
        );
      }
      if(projectName.length > 200) {
        throw new InvalidRequestProjectNameMaxSizeError(
          200,
          `Attribute projectName is too long. Size is ${projectName.length}. Max size is 200.`
        );
      }
        if(projectName.trim().length === 0) {
          throw new InvalidRequestProjectNameNonEmptyError(
            `Attribute projectName cannot be an empty string.`
          );
        }
    }
    private static validateCreator = (creator: string): void => {
      if(creator.length < 1) {
        throw new InvalidRequestCreatorMinSizeError(
          1,
          `Attribute creator is too short. Size is ${creator.length}. Min size is 1.`
        );
      }
      if(creator.length > 200) {
        throw new InvalidRequestCreatorMaxSizeError(
          200,
          `Attribute creator is too long. Size is ${creator.length}. Max size is 200.`
        );
      }
        if(creator.trim().length === 0) {
          throw new InvalidRequestCreatorNonEmptyError(
            `Attribute creator cannot be an empty string.`
          );
        }
    }
    private static validateSurveys = (surveys: string): void => {
      if(surveys.length < 1) {
        throw new InvalidRequestSurveysMinSizeError(
          1,
          `Attribute surveys is too short. Size is ${surveys.length}. Min size is 1.`
        );
      }
      if(surveys.length > 200) {
        throw new InvalidRequestSurveysMaxSizeError(
          200,
          `Attribute surveys is too long. Size is ${surveys.length}. Max size is 200.`
        );
      }
        if(surveys.trim().length === 0) {
          throw new InvalidRequestSurveysNonEmptyError(
            `Attribute surveys cannot be an empty string.`
          );
        }
    }
    private static validateBoUserId = (boUserId: string): void => {
      if(boUserId.length < 3) {
        throw new InvalidRequestBoUserIdMinSizeError(
          3,
          `Attribute boUserId is too short. Size is ${boUserId.length}. Min size is 3.`
        );
      }
      if(boUserId.length > 200) {
        throw new InvalidRequestBoUserIdMaxSizeError(
          200,
          `Attribute boUserId is too long. Size is ${boUserId.length}. Max size is 200.`
        );
      }
        if(boUserId.trim().length === 0) {
          throw new InvalidRequestBoUserIdNonEmptyError(
            `Attribute boUserId cannot be an empty string.`
          );
        }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetArchivedResearcherProjectsRPC(sortList='${this.sortList}',projectName='${this.projectName}',creator='${this.creator}',surveys='${this.surveys}',createdOnStartDate='${this.createdOnStartDate}',createdOnEndDate='${this.createdOnEndDate}',lastModifiedStartDate='${this.lastModifiedStartDate}',lastModifiedEndDate='${this.lastModifiedEndDate}',boUserId='${this.boUserId}',itemsPerPage='${this.itemsPerPage}',pageIndex='${this.pageIndex}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    getArchivedResearcherProjectsPaginationResponse: GetArchivedResearcherProjectsPaginationResponseDTO[];
    totalItems: number;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetArchivedResearcherProjectsResponse: boolean = true;
  
    readonly getArchivedResearcherProjectsPaginationResponse: GetArchivedResearcherProjectsPaginationResponse[];
    readonly totalItems: number;
  
  
    constructor(
    getArchivedResearcherProjectsPaginationResponse: GetArchivedResearcherProjectsPaginationResponse[], 
    totalItems: number
    ) {
      this.getArchivedResearcherProjectsPaginationResponse = getArchivedResearcherProjectsPaginationResponse;
      this.totalItems = totalItems;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const getArchivedResearcherProjectsPaginationResponse = getList<GetArchivedResearcherProjectsPaginationResponseDTO, GetArchivedResearcherProjectsPaginationResponse>(
        dto,
        "getArchivedResearcherProjectsPaginationResponse",
        GetArchivedResearcherProjectsPaginationResponse.fromDTO,
        InvalidResponseGetArchivedResearcherProjectsPaginationResponseError
      );
      const totalItems = getInt32(dto, "totalItems", InvalidResponseTotalItemsError);
      return new Response(
        getArchivedResearcherProjectsPaginationResponse,
        totalItems
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          getArchivedResearcherProjectsPaginationResponse: this.getArchivedResearcherProjectsPaginationResponse.map((e: GetArchivedResearcherProjectsPaginationResponse) => { return e.toDTO() }),
          totalItems: this.totalItems
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetArchivedResearcherProjects(getArchivedResearcherProjectsPaginationResponse='${this.getArchivedResearcherProjectsPaginationResponse}',totalItems='${this.totalItems}')`;
  };
  

  export enum RPCError {
    InvalidPageIndex = "INVALID_PAGE_INDEX",
    InvalidDateRange = "INVALID_DATE_RANGE"
  }

  export namespace Errors {
    
    export interface InvalidPageIndexDTO {
      code: string;
    }
    
    export class InvalidPageIndex extends LeoRPCError<RPCError> {
      readonly isInvalidPageIndex: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidPageIndex;
        super(code);
      }
    
    
      toDTO(): InvalidPageIndexDTO {
        const dto: InvalidPageIndexDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidPageIndex.prototype.toString = function toString(): string {
      return `GetArchivedResearcherProjectsRPC.INVALID_PAGE_INDEX()`;
    };
    
    
    export interface InvalidDateRangeDTO {
      code: string;
    }
    
    export class InvalidDateRange extends LeoRPCError<RPCError> {
      readonly isInvalidDateRange: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidDateRange;
        super(code);
      }
    
    
      toDTO(): InvalidDateRangeDTO {
        const dto: InvalidDateRangeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidDateRange.prototype.toString = function toString(): string {
      return `GetArchivedResearcherProjectsRPC.INVALID_DATE_RANGE()`;
    };
    

    export type Errors = InvalidPageIndex | InvalidDateRange;
    export type ErrorsDTO = InvalidPageIndexDTO | InvalidDateRangeDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidPageIndex:
          return new InvalidPageIndex();
        case RPCError.InvalidDateRange:
          return new InvalidDateRange();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSortListError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortListError";
  }
}

export class InvalidRequestProjectNameError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectNameError";
  }
}

export class InvalidRequestProjectNameMaxSizeError extends InvalidRequestProjectNameError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectNameMaxSizeError";
  }
}

export class InvalidRequestProjectNameMinSizeError extends InvalidRequestProjectNameError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectNameMinSizeError";
  }
}

export class InvalidRequestProjectNameNonEmptyError extends InvalidRequestProjectNameError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectNameNonEmptyError";
  }
}

export class InvalidRequestCreatorError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCreatorError";
  }
}

export class InvalidRequestCreatorMaxSizeError extends InvalidRequestCreatorError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCreatorMaxSizeError";
  }
}

export class InvalidRequestCreatorMinSizeError extends InvalidRequestCreatorError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCreatorMinSizeError";
  }
}

export class InvalidRequestCreatorNonEmptyError extends InvalidRequestCreatorError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCreatorNonEmptyError";
  }
}

export class InvalidRequestSurveysError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveysError";
  }
}

export class InvalidRequestSurveysMaxSizeError extends InvalidRequestSurveysError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveysMaxSizeError";
  }
}

export class InvalidRequestSurveysMinSizeError extends InvalidRequestSurveysError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveysMinSizeError";
  }
}

export class InvalidRequestSurveysNonEmptyError extends InvalidRequestSurveysError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveysNonEmptyError";
  }
}

export class InvalidRequestCreatedOnStartDateError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCreatedOnStartDateError";
  }
}

export class InvalidRequestCreatedOnEndDateError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCreatedOnEndDateError";
  }
}

export class InvalidRequestLastModifiedStartDateError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLastModifiedStartDateError";
  }
}

export class InvalidRequestLastModifiedEndDateError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLastModifiedEndDateError";
  }
}

export class InvalidRequestBoUserIdError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUserIdError";
  }
}

export class InvalidRequestBoUserIdMaxSizeError extends InvalidRequestBoUserIdError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUserIdMaxSizeError";
  }
}

export class InvalidRequestBoUserIdMinSizeError extends InvalidRequestBoUserIdError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUserIdMinSizeError";
  }
}

export class InvalidRequestBoUserIdNonEmptyError extends InvalidRequestBoUserIdError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUserIdNonEmptyError";
  }
}

export class InvalidRequestItemsPerPageError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestPageIndexError extends GetArchivedResearcherProjectsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidResponseGetArchivedResearcherProjectsPaginationResponseError extends GetArchivedResearcherProjectsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseGetArchivedResearcherProjectsPaginationResponseError";
  }
}

export class InvalidResponseTotalItemsError extends GetArchivedResearcherProjectsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsError";
  }
}

export class InvalidResponseInvalidPageIndexError extends GetArchivedResearcherProjectsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidPageIndexError";
  }
}

export class InvalidResponseInvalidDateRangeError extends GetArchivedResearcherProjectsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidDateRangeError";
  }
}

